import React from 'react';
import { useTranslationStore } from '../store/translationStore';
import SpeakerButton from './SpeakerButton';
import MonkeyLoader from './MonkeyLoader';

const TranslationCard: React.FC = () => {
  // CSS keyframes for progressive loading animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes tts-pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(1.05);
        }
      }

      /* Ensure animations respect user's motion preferences */
      @media (prefers-reduced-motion: reduce) {
        @keyframes fadeInUp {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes tts-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const {
    currentTranslation,
    loading
  } = useTranslationStore();

  // Helper function to check if detailed content should be shown
  const hasDetailedContent = () => {
    if (!currentTranslation) return false;
    return (
      (currentTranslation.alternatives && currentTranslation.alternatives.length > 0) ||
      (currentTranslation.examples && currentTranslation.examples.length > 0) ||
      currentTranslation.category
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto">
        <MonkeyLoader size="md" />
      </div>
    );
  }

  // Show empty state when not loading and no translation
  if (!currentTranslation && !loading) {
    return (
      <div className="max-w-2xl mx-auto py-8 text-center">
        <p className="text-gray-500 text-base">Enter text above to see translation</p>
      </div>
    );
  }

  // Don't render anything if we don't have a translation
  if (!currentTranslation) {
    return null;
  }

  return (
    <div
      className="max-w-2xl mx-auto py-6 md:py-8 px-4 md:px-0"
      style={{
        animation: !loading ? 'fadeInUp 0.4s ease-out' : 'none'
      }}
    >
      {/* English */}
      <div className="mb-4 md:mb-6">
        <h3 className="text-sm font-medium text-gray-500 mb-2">English</h3>
        <p className="text-lg text-gray-900">{currentTranslation.original}</p>
      </div>

      {/* Chinese Translation */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Chinese Translation</h3>
        <div className="flex items-start gap-3">
          <p className="text-xl md:text-2xl font-medium text-gray-900 transition-all duration-200 ease-in-out break-words flex-1">
            {currentTranslation.translation}
            {currentTranslation.pinyin && (
              <span className="text-base md:text-lg text-gray-600 ml-2 inline">
                ({currentTranslation.pinyin})
              </span>
            )}
          </p>
          <div className="flex-shrink-0 mt-1">
            <SpeakerButton
              text={currentTranslation.translation}
              size="md"
              className="md:scale-110"
            />
          </div>
        </div>
      </div>

      {/* Separator between basic and detailed content */}
      {hasDetailedContent() && (
        <div className="border-t border-gray-100 my-6 md:my-8"></div>
      )}

      {/* Alternative Variations */}
      {currentTranslation.alternatives && currentTranslation.alternatives.length > 0 && (() => {
        const uniqueAlternatives = currentTranslation.alternatives.filter(alt =>
          alt.term !== currentTranslation.translation &&
          alt.term !== currentTranslation.officialTranslation
        );

        return uniqueAlternatives.length > 0 ? (
          <div className="mb-6 md:mb-8 transition-opacity duration-300 ease-in-out">
            <h3 className="text-sm font-medium text-gray-500 mb-4">Alternatives</h3>
            <div className="space-y-4">
              {uniqueAlternatives.map((alt, index) => (
                <div
                  key={index}
                  className="pb-3 border-b border-gray-100 last:border-b-0 transform transition-all duration-200 ease-in-out"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.3s ease-out forwards'
                  }}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="flex items-baseline">
                      <span className="text-lg font-medium text-gray-900">{alt.term}</span>
                      {alt.pinyin && (
                        <span className="text-sm text-gray-500 ml-2">({alt.pinyin})</span>
                      )}
                    </div>
                    <SpeakerButton
                      text={alt.term}
                      size="sm"
                      className="ml-2"
                    />
                  </div>
                  <p className="text-sm text-gray-600">{alt.context}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* Examples */}
      {currentTranslation.examples && currentTranslation.examples.length > 0 && (
        <div className="mb-6 md:mb-8 transition-opacity duration-300 ease-in-out">
          <h3 className="text-sm font-medium text-gray-500 mb-4">Example</h3>
          <div className="space-y-6">
            {currentTranslation.examples.map((example, index) => {
              // Parse example format: "English sentence - 中文句子 - pinyin"
              const parts = example.split(' - ');
              if (parts.length === 3) {
                const englishPart = parts[0].trim();
                const chinesePart = parts[1].trim();
                const pinyinPart = parts[2].trim();

                return (
                  <div
                    key={index}
                    className="border-l-4 border-blue-100 pl-4 transform transition-all duration-200 ease-in-out"
                    style={{
                      animationDelay: `${(index + 3) * 100}ms`, // Delay after alternatives
                      animation: 'fadeInUp 0.3s ease-out forwards'
                    }}
                  >
                    {/* Chinese phrase */}
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-lg font-medium text-gray-900 flex-1">
                        {chinesePart}
                      </p>
                      <SpeakerButton
                        text={chinesePart}
                        size="sm"
                      />
                    </div>

                    {/* Pinyin */}
                    <p className="text-sm text-gray-600 italic mb-2">
                      {pinyinPart}
                    </p>

                    {/* English meaning */}
                    <p className="text-base text-gray-700">
                      {englishPart}
                    </p>
                  </div>
                );
              } else {
                // Fallback for examples that don't match expected format
                return (
                  <div
                    key={index}
                    className="border-l-4 border-blue-100 pl-4 transform transition-all duration-200 ease-in-out"
                    style={{
                      animationDelay: `${(index + 3) * 100}ms`,
                      animation: 'fadeInUp 0.3s ease-out forwards'
                    }}
                  >
                    <p className="text-base text-gray-700 leading-relaxed">
                      {example}
                    </p>
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}

      {/* Category */}
      {currentTranslation.category && (
        <div
          className="inline-block px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full transition-all duration-300 ease-in-out"
          style={{
            animationDelay: '600ms',
            animation: 'fadeInUp 0.3s ease-out forwards'
          }}
        >
          {currentTranslation.category}
        </div>
      )}
    </div>
  );
};

export default TranslationCard;